import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Row, Col, Form, Button, Badge, Spinner, Alert, Modal, ListGroup } from 'react-bootstrap';
import { InteractiveNvlWrapper } from '@neo4j-nvl/react';
import { knowledgeGraphApi, pdfApi } from '../services/api';

const CATEGORY_COLORS = {
  'General Policies': '#6c757d',
  'Board of Trustees Policies': '#0d6efd',
  'Fiscal Policies': '#198754',
  'Human Resources Policies': '#fd7e14',
  'Information Technology Policies': '#6f42c1',
  'Research Policies': '#dc3545',
  'Health & Safety / Emergency Management Policies': '#d63384'
};

function KnowledgeGraphTab() {
  const DEFAULT_MAX_DEPTH = 1;
  const DEFAULT_MAX_NODES = 60;
  const DEFAULT_MAX_EDGES = 120;

  const [nvlNodes, setNvlNodes] = useState([]);
  const [nvlRels, setNvlRels] = useState([]);
  const [nodeMap, setNodeMap] = useState({});
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPolicy, setSelectedPolicy] = useState('');
  const [maxDepth, setMaxDepth] = useState(DEFAULT_MAX_DEPTH);
  
  // Node details modal
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeConnections, setNodeConnections] = useState({ incoming: [], outgoing: [] });

  // Neo4j browser embed
  const [showNeo4jBrowser, setShowNeo4jBrowser] = useState(false);
  
  const nvlRef = useRef();

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load stats and categories in parallel
      const [statsData, categoriesData] = await Promise.all([
        knowledgeGraphApi.getStats(),
        knowledgeGraphApi.getCategories()
      ]);
      
      setStats(statsData);
      setCategories(categoriesData.categories || []);
      
      // Load initial graph
      await loadGraph({
        max_depth: maxDepth,
        max_nodes: DEFAULT_MAX_NODES,
        max_edges: DEFAULT_MAX_EDGES,
      });
      
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError(err.response?.data?.detail || 'Failed to load knowledge graph data');
    } finally {
      setLoading(false);
    }
  };
  const loadGraph = async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await knowledgeGraphApi.getGraph(filters);
      
      const nodes = data.nodes.map(node => ({
        id: node.id,
        caption: node.title || node.label || node.id,
        color: CATEGORY_COLORS[node.category] || '#999',
        size: 20,
        _label: node.label,
        _category: node.category,
        _title: node.title,
      }));
      const rels = data.edges.map((edge, idx) => ({
        id: edge.id || `rel-${idx}`,
        from: edge.source,
        to: edge.target,
        caption: edge.type || '',
      }));
      const map = {};
      nodes.forEach(n => { map[n.id] = n; });
      setNvlNodes(nodes);
      setNvlRels(rels);
      setNodeMap(map);
      
    } catch (err) {
      console.error('Error loading graph:', err);
      setError(err.response?.data?.detail || 'Failed to load graph');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = useCallback(() => {
    const filters = {
      max_nodes: DEFAULT_MAX_NODES,
      max_edges: DEFAULT_MAX_EDGES,
    };
    if (selectedCategory) filters.category = selectedCategory;
    if (selectedPolicy) filters.policy_id = selectedPolicy;
    if (maxDepth) filters.max_depth = maxDepth;
    
    loadGraph(filters);
  }, [selectedCategory, selectedPolicy, maxDepth]);

  const handleResetFilters = () => {
    setSelectedCategory('');
    setSelectedPolicy('');
    setMaxDepth(DEFAULT_MAX_DEPTH);
    loadGraph({
      max_depth: DEFAULT_MAX_DEPTH,
      max_nodes: DEFAULT_MAX_NODES,
      max_edges: DEFAULT_MAX_EDGES,
    });
  };

  const handleNodeClick = useCallback((node) => {
    const incoming = nvlRels
      .filter(r => r.to === node.id)
      .map(r => nodeMap[r.from])
      .filter(Boolean);
    const outgoing = nvlRels
      .filter(r => r.from === node.id)
      .map(r => nodeMap[r.to])
      .filter(Boolean);
    setSelectedNode(node);
    setNodeConnections({ incoming, outgoing });
    setShowNodeModal(true);
    if (nvlRef.current) {
      nvlRef.current.zoomToNodes([node.id], { duration: 600 });
    }
  }, [nvlRels, nodeMap]);

  const handleViewPDF = (filename) => {
    window.open(pdfApi.getPDFUrl(filename), '_blank');
  };

  const handleExploreNetwork = (nodeId) => {
    setShowNodeModal(false);
    setSelectedPolicy(nodeId);
    loadGraph({
      policy_id: nodeId,
      max_depth: maxDepth,
      max_nodes: DEFAULT_MAX_NODES,
      max_edges: DEFAULT_MAX_EDGES,
    });
  };

  const mouseEventCallbacks = {
    onNodeClick: (node) => handleNodeClick(node),
    onZoom: true,
    onPan: true,
  };

  const nvlOptions = {
    initialZoom: 0.8,
    layout: 'forceDirected',
  };

  return (
    <div className="knowledge-graph-tab">
      <Row className="mb-3">
        <Col md={8}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-project-diagram me-2"></i>
                Policy Knowledge Graph
              </h5>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              
              {nvlNodes.length > 50 && (
                <Alert variant="info" dismissible>
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Performance Tip:</strong> Viewing {nvlNodes.length} nodes. 
                  Use filters to view specific categories for better performance.
                </Alert>
              )}
              
              {loading && !nvlNodes.length ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Loading knowledge graph...</p>
                </div>
              ) : (
                <div style={{ height: '600px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                  <InteractiveNvlWrapper
                    ref={nvlRef}
                    nodes={nvlNodes}
                    rels={nvlRels}
                    nvlOptions={nvlOptions}
                    mouseEventCallbacks={mouseEventCallbacks}
                    style={{ width: '100%', height: '100%' }}
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          {/* Statistics */}
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">
                <i className="fas fa-chart-bar me-2"></i>
                Graph Statistics
              </h6>
            </Card.Header>
            <Card.Body>
              {stats ? (
                <>
                  <div className="mb-2">
                    <strong>Total Policies:</strong> {stats.total_policies}
                  </div>
                  <div className="mb-2">
                    <strong>Current View:</strong>
                  </div>
                  <div className="mb-2">
                    <Badge bg="primary" className="me-1">{nvlNodes.length}</Badge> Nodes
                    <Badge bg="secondary" className="ms-2">{nvlRels.length}</Badge> Links
                  </div>
                  <hr />
                  <div className="mb-2">
                    <strong>By Category:</strong>
                  </div>
                  {stats.categories.map(cat => (
                    <div key={cat.category} className="mb-1">
                      <Badge 
                        bg="light" 
                        text="dark"
                        style={{ 
                          backgroundColor: CATEGORY_COLORS[cat.category],
                          color: 'white'
                        }}
                      >
                        {cat.count}
                      </Badge>
                      {' '}
                      <small>{cat.category}</small>
                    </div>
                  ))}
                </>
              ) : (
                <Spinner animation="border" size="sm" />
              )}
            </Card.Body>
          </Card>

          {/* Filters */}
          <Card>
            <Card.Header>
              <h6 className="mb-0">
                <i className="fas fa-filter me-2"></i>
                Filters
              </h6>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Policy ID</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., BT0001"
                    value={selectedPolicy}
                    onChange={(e) => setSelectedPolicy(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Show network for specific policy
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Max Depth: {maxDepth}</Form.Label>
                  <Form.Range
                    min={1}
                    max={5}
                    value={maxDepth}
                    onChange={(e) => setMaxDepth(parseInt(e.target.value))}
                  />
                  <Form.Text className="text-muted">
                    Relationship depth for policy networks
                  </Form.Text>
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button 
                    variant="primary" 
                    onClick={handleFilterChange}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-search me-2"></i>
                        Apply Filters
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    onClick={handleResetFilters}
                    disabled={loading}
                  >
                    <i className="fas fa-undo me-2"></i>
                    Reset
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          {/* Legend */}
          <Card className="mt-3">
            <Card.Header>
              <h6 className="mb-0">
                <i className="fas fa-info-circle me-2"></i>
                Legend
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-2">
                <strong>Categories:</strong>
              </div>
              {Object.entries(CATEGORY_COLORS).map(([category, color]) => (
                <div key={category} className="mb-1">
                  <span 
                    style={{
                      display: 'inline-block',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: color,
                      marginRight: '8px'
                    }}
                  ></span>
                  <small>{category}</small>
                </div>
              ))}
              <hr />
              <div className="mb-1">
                <i className="fas fa-arrow-right me-2"></i>
                <small>References relationship</small>
              </div>
              <div className="mb-1">
                <i className="fas fa-mouse-pointer me-2"></i>
                <small>Click node for details & PDF</small>
              </div>
              <div className="mb-1">
                <i className="fas fa-search-plus me-2"></i>
                <small>Scroll to zoom, drag to pan</small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Node Details Modal */}
      <Modal show={showNodeModal} onHide={() => setShowNodeModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-file-alt me-2"></i>
            {selectedNode?._title || selectedNode?.caption || 'Policy Details'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedNode && (
            <>
              <div className="mb-3">
                <strong>Policy ID:</strong> {selectedNode.id}<br />
                <strong>Filename:</strong> {selectedNode._label || selectedNode.caption}<br />
                <strong>Category:</strong>{' '}
                <Badge 
                  style={{ 
                    backgroundColor: selectedNode.color || '#999',
                    color: 'white'
                  }}
                >
                  {selectedNode._category}
                </Badge>
              </div>

              <div className="mb-3">
                <h6>Connections ({nodeConnections.incoming.length + nodeConnections.outgoing.length} total)</h6>
                
                {nodeConnections.incoming.length > 0 && (
                  <div className="mb-2">
                    <strong className="text-success">
                      <i className="fas fa-arrow-left me-2"></i>
                      Referenced By ({nodeConnections.incoming.length}):
                    </strong>
                    <ListGroup variant="flush" className="mt-2">
                      {nodeConnections.incoming.slice(0, 5).map(node => (
                        <ListGroup.Item 
                          key={node.id}
                          action
                          onClick={() => {
                            setShowNodeModal(false);
                            setTimeout(() => handleNodeClick(node), 100);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <small>
                            <Badge 
                              bg="light" 
                              text="dark"
                              style={{ 
                                  backgroundColor: node.color || '#999',
                                color: 'white',
                                marginRight: '8px'
                              }}
                            >
                                {(node._category || '').split(' ')[0]}
                            </Badge>
                              {node._title || node.caption}
                          </small>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                    {nodeConnections.incoming.length > 5 && (
                      <small className="text-muted">
                        ...and {nodeConnections.incoming.length - 5} more
                      </small>
                    )}
                  </div>
                )}

                {nodeConnections.outgoing.length > 0 && (
                  <div className="mb-2">
                    <strong className="text-primary">
                      <i className="fas fa-arrow-right me-2"></i>
                      References ({nodeConnections.outgoing.length}):
                    </strong>
                    <ListGroup variant="flush" className="mt-2">
                      {nodeConnections.outgoing.slice(0, 5).map(node => (
                        <ListGroup.Item 
                          key={node.id}
                          action
                          onClick={() => {
                            setShowNodeModal(false);
                            setTimeout(() => handleNodeClick(node), 100);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <small>
                            <Badge 
                              bg="light" 
                              text="dark"
                              style={{ 
                                  backgroundColor: node.color || '#999',
                                color: 'white',
                                marginRight: '8px'
                              }}
                            >
                                {(node._category || '').split(' ')[0]}
                            </Badge>
                              {node._title || node.caption}
                          </small>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                    {nodeConnections.outgoing.length > 5 && (
                      <small className="text-muted">
                        ...and {nodeConnections.outgoing.length - 5} more
                      </small>
                    )}
                  </div>
                )}

                {nodeConnections.incoming.length === 0 && nodeConnections.outgoing.length === 0 && (
                  <Alert variant="info">
                    <i className="fas fa-info-circle me-2"></i>
                    No connections found for this policy
                  </Alert>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowNodeModal(false)}
          >
            Close
          </Button>
          <Button 
            variant="info" 
            onClick={() => selectedNode && handleExploreNetwork(selectedNode.id)}
          >
            <i className="fas fa-project-diagram me-2"></i>
            Explore Network
          </Button>
          <Button 
            variant="primary" 
            onClick={() => selectedNode && handleViewPDF(selectedNode._label || selectedNode.caption)}
          >
            <i className="fas fa-file-pdf me-2"></i>
            View PDF
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Neo4j Browser */}
      <Row className="mt-3">
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-database me-2"></i>
                Neo4j Database Browser
              </h5>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  href="https://viridian.ise.utk.edu:7473/browser/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-external-link-alt me-1"></i>
                  Open in New Tab
                </Button>
                <Button
                  variant={showNeo4jBrowser ? 'secondary' : 'primary'}
                  size="sm"
                  onClick={() => setShowNeo4jBrowser(v => !v)}
                >
                  <i className={`fas fa-${showNeo4jBrowser ? 'compress-alt' : 'expand-alt'} me-1`}></i>
                  {showNeo4jBrowser ? 'Hide Browser' : 'Show Browser'}
                </Button>
              </div>
            </Card.Header>

            {showNeo4jBrowser && (
              <Card.Body className="p-0">
                <iframe
                  src="https://viridian.ise.utk.edu:7473/browser/"
                  title="Neo4j Database Browser"
                  style={{ width: '100%', height: '750px', border: 'none' }}
                  allow="fullscreen"
                />
              </Card.Body>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default KnowledgeGraphTab;
